import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { DateUtil } from '@utils/dateUtil'
import { createGenericFetchError, getErrorMessage, isFetchBaseError } from '@utils/errorUtil'

const POLLING = {
  maxWaitTime: 30 * 1000,
  pollingInterval: 1000,
} as const

type ControllerService = {
  fetchClaimRecord: (claimRecordId: string) => Promise<ClaimRecord>
}

type ControllerOptions = {
  maxWaitTime?: number
  pollingInterval?: number
}

type PollingResult = {
  iterationCount: number
  status: 'stopped' | 'time-exceeded' | 'ready' | 'error'
}

type ControllerEventHandlers = {
  isReady: (claimRecord: ClaimRecord) => boolean
  onReady: (claimRecord: ClaimRecord) => void
  onTimeExceeded: (claimRecord: ClaimRecord) => void
  onError: (error: FetchBaseQueryError) => void
}

export type PollClaimRecordControllerReturn = {
  start: (handlers: ControllerEventHandlers, claimRecordId: string) => Promise<PollingResult>
  stop: () => void
}

export const pollClaimRecordController = (
  service: ControllerService,
  params: ControllerOptions
): PollClaimRecordControllerReturn => {
  const { maxWaitTime = POLLING.maxWaitTime, pollingInterval = POLLING.pollingInterval } = params

  let isStop = false
  let iterationCount = 0
  let timeout: NodeJS.Timeout

  const start = async (handlers: ControllerEventHandlers, claimRecordId: string): Promise<PollingResult> => {
    isStop = false
    iterationCount = 1
    const startTime = new Date()

    return new Promise<PollingResult>(resolve => {
      const poll = async () => {
        try {
          const claimRecord = await service.fetchClaimRecord(claimRecordId)

          if (isStop) {
            clearTimeout(timeout)
            resolve({ iterationCount, status: 'stopped' })
            return
          }

          if (handlers.isReady(claimRecord)) {
            clearTimeout(timeout)
            handlers.onReady(claimRecord)
            resolve({ iterationCount, status: 'ready' })
          } else if (DateUtil.isMaxTimeExceeded({ startTime, maxTime: maxWaitTime })) {
            clearTimeout(timeout)
            handlers.onTimeExceeded(claimRecord)
            resolve({ iterationCount, status: 'time-exceeded' })
          } else {
            iterationCount += 1
            timeout = setTimeout(poll, pollingInterval)
          }
        } catch (error) {
          clearTimeout(timeout)
          handleError(handlers, error)
          resolve({ iterationCount, status: 'error' })
        }
      }

      // start polling
      return poll()
    })
  }

  const handleError = async (handlers: ControllerEventHandlers, error: unknown) => {
    if (isFetchBaseError(error)) {
      handlers.onError(error)
    } else {
      handlers.onError(createGenericFetchError(getErrorMessage(error) ?? 'Poll claim record error'))
    }
  }

  const stop = () => {
    isStop = true
    clearTimeout(timeout)
  }

  return {
    start,
    stop,
  }
}
